import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Appointment } from '@principle-theorem/principle-core';
import {
  DepositRequestDeliveryChannel,
  DepositRequestOutcome,
  DepositStatus,
  type IAppointment,
  type ICreateDepositRequestParams,
  type ICreateDepositRequestResponse,
  type IDepositMetadata,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  httpsCallable,
  isObject,
  serialise,
  snapshot,
  uid,
  type SerialisedData,
  unserialise,
  type WithRef,
} from '@principle-theorem/shared';
import { isString } from 'lodash';
import { throwError } from 'rxjs';
import { timeoutWith } from 'rxjs/operators';
import { buildSavedAppointmentPaymentLinkParams } from './appointment-deposit-payment-link';
import { type ISendPaymentLinkParams } from './deposit.store';

/**
 * Bounds the send so the operator always hears an outcome. The dialog that
 * started the send has already closed, so a hang would otherwise vanish
 * silently rather than freeze anything visible.
 */
const sendTimeoutMs = 30_000;

const sendTimeoutMessage =
  'Sending the payment link took too long. Please try again.';
const sendFailureMessage = 'Failed to send payment link';
const linkCreatedNotSentMessage =
  'The payment link was created but could not be sent';
const paymentLinkSentMessage = 'Deposit payment link sent';

function sendFailureText(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  if (isObject(error) && isString(error.message)) {
    return error.message;
  }
  return sendFailureMessage;
}

const sendingMessages: Record<DepositRequestDeliveryChannel, string> = {
  [DepositRequestDeliveryChannel.Email]:
    'Sending deposit payment link by email…',
  [DepositRequestDeliveryChannel.SMS]: 'Sending deposit payment link by SMS…',
};

/**
 * Sends a deposit payment link for a saved appointment without holding any
 * surface open while it happens.
 *
 * The Take Deposit dialog closes the moment a channel is chosen, so this work
 * cannot live in the dialog: its component-provided store is destroyed with
 * it, which would cancel the send mid-flight. Root scope is what lets the
 * send outlive the surface that started it, with snackbars carrying the
 * "sending" and "sent"/failure states to the operator wherever they are now.
 */
@Injectable({ providedIn: 'root' })
export class SendAppointmentDepositLinkService {
  constructor(private _snackBar: MatSnackBar) {}

  /**
   * Resolves once the outcome snackbar has been chosen; callers are not
   * expected to await it. The deposit only advances to Pending after the
   * server confirms delivery, so a failed send never claims a link is out.
   */
  async send(
    appointment: WithRef<IAppointment>,
    deliveryChannel: DepositRequestDeliveryChannel
  ): Promise<void> {
    if (!appointment.deposit) {
      return;
    }
    this._snackBar.open(sendingMessages[deliveryChannel], undefined, {
      duration: 5000,
    });
    try {
      const params = await this._buildParams(appointment, deliveryChannel);
      if (!params) {
        this._snackBar.open(sendFailureMessage);
        return;
      }
      const serialisedResponse = await snapshot(
        httpsCallable<
          SerialisedData<ICreateDepositRequestParams>,
          SerialisedData<ICreateDepositRequestResponse>
        >('http-stripe-createDepositRequest')(serialise(params)).pipe(
          timeoutWith(sendTimeoutMs, throwError(new Error(sendTimeoutMessage)))
        )
      );
      const response = unserialise(serialisedResponse);
      if (response.deliveryStatus === DepositRequestOutcome.Failed) {
        this._snackBar.open(
          response.deliveryError ?? linkCreatedNotSentMessage
        );
        return;
      }
      await this._markDepositPending(appointment);
      this._snackBar.open(paymentLinkSentMessage, undefined, {
        duration: 5000,
      });
    } catch (error) {
      this._snackBar.open(sendFailureText(error));
    }
  }

  private async _buildParams(
    appointment: WithRef<IAppointment>,
    deliveryChannel: DepositRequestDeliveryChannel
  ): Promise<ISendPaymentLinkParams | undefined> {
    const patient = await snapshot(Appointment.patient$(appointment));
    return buildSavedAppointmentPaymentLinkParams({
      appointment,
      patient,
      deliveryChannel,
      requestKey: uid(),
    });
  }

  private async _markDepositPending(
    appointment: WithRef<IAppointment>
  ): Promise<void> {
    if (!appointment.deposit) {
      return;
    }
    const updatedDeposit: IDepositMetadata = {
      ...appointment.deposit.metadata,
      status: DepositStatus.Pending,
    };
    await Firestore.patchDoc(appointment.ref, {
      deposit: { ...appointment.deposit, metadata: updatedDeposit },
    });
  }
}
